import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { Download, Trash2, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner'; // ✅ Utiliser Sonner

const MyData = () => {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);

    try {
      const token = await auth.currentUser.getIdToken();

      const url = 'https://exportuserdata-5zm2gcbnlq-uc.a.run.app';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: {} }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response:', errorText);
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      const exportData = result.result;

      // Télécharger JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      const url2 = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      a.download = `remadia-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url2);

      toast.success('Données exportées avec succès');
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Mes données personnelles</h1>

      {/* Info RGPD */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-semibold text-blue-900 mb-2">Protection de vos données</h2>
            <p className="text-blue-800 text-sm">
              Vos conversations médicales sont chiffrées et sécurisées. Conformément au RGPD, vous
              avez le droit d'accéder, de modifier et de supprimer vos données personnelles.
            </p>
          </div>
        </div>
      </div>

      {/* Conservation */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Clock className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-semibold text-gray-900 mb-2">Durée de conservation</h2>
            <p className="text-gray-600 text-sm">
              Vos messages sont automatiquement supprimés après <strong>2 ans</strong>à compter de
              leur création. Cette suppression est irréversible.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        {/* Export */}
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Exporter mes données
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Téléchargez une copie complète de vos données (profil, messages, historique) au
                format JSON.
              </p>
              <button
                onClick={handleExportData}
                disabled={exporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? 'Export en cours...' : 'Télécharger mes données'}
              </button>
            </div>
          </div>
        </div>

        {/* Suppression compte → déléguée à la page Paramètres (porte officielle) */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Supprimer mon compte
              </h3>
              <p className="text-red-800 text-sm mb-4">
                La suppression de compte s'effectue désormais depuis la page{' '}
                <strong>Paramètres</strong>, section « Supprimer votre compte » (confirmation
                par mot de passe requise). Pensez à exporter vos données avant.
              </p>
              <button
                onClick={() => navigate('/settings')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Aller à la suppression de compte
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
        <p>
          <strong>Questions sur vos données ?</strong>
          <br />
          Contactez notre DPO à :{' '}
          <a href="mailto:dpo@remadia.com" className="text-blue-600 hover:underline">
            dpo@remadia.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default MyData;
